import { createSlice, type PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { Notification } from '@/types/dashboard';
import { v2Api } from '@/lib/api';

export interface NotificationsState {
  items: Notification[];
  loading: boolean;
  error: string | null;
  count: number;
  next: string | null;
  previous: string | null;
}

const initialState: NotificationsState = {
  items: [],
  loading: false,
  error: null,
  count: 0,
  next: null,
  previous: null,
};

export const markNotificationRead = createAsyncThunk(
  'notifications/markRead',
  async (notification: Notification, { rejectWithValue }) => {
    try {
      if (notification.id) {
        await v2Api.dashboard.notifications.markRead(notification.id);
      }
      return notification;
    } catch (err) {
      console.error('Error marking notification as read:', err);
      return rejectWithValue(err);
    }
  },
);

export const markNotificationUnread = createAsyncThunk(
  'notifications/markUnread',
  async (notification: Notification, { rejectWithValue }) => {
    try {
      if (notification.id) {
        await v2Api.dashboard.notifications.markUnread(notification.id);
      }
      return notification;
    } catch (err) {
      console.error('Error marking notification as unread:', err);
      return rejectWithValue(err);
    }
  },
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (userId: string | undefined, { rejectWithValue }) => {
    try {
      await v2Api.dashboard.notifications.markAllRead(userId);
      return { userId };
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      return rejectWithValue(err);
    }
  },
);

export const deleteNotificationThunk = createAsyncThunk(
  'notifications/delete',
  async (notification: Notification, { rejectWithValue }) => {
    try {
      if (notification.id) {
        await v2Api.dashboard.notifications.delete(notification.id);
      }
      return notification;
    } catch (err) {
      console.error('Error deleting notification:', err);
      return rejectWithValue(err);
    }
  },
);

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    notificationsRequested(state) {
      state.loading = true;
      state.error = null;
    },
    notificationsReceived(
      state,
      action: PayloadAction<{
        items: Notification[];
        count: number;
        next: string | null;
        previous: string | null;
      }>,
    ) {
      state.items = action.payload.items;
      state.count = action.payload.count;
      state.next = action.payload.next;
      state.previous = action.payload.previous;
      state.loading = false;
    },
    notificationsFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
      state.items = [];
      state.count = 0;
      state.next = null;
      state.previous = null;
    },
    notificationMarkedRead(state, action: PayloadAction<Notification>) {
      const notif = action.payload;
      state.items = state.items.map((n: Notification) =>
        (notif.id && n.id === notif.id) || (!notif.id && n.timestamp === notif.timestamp)
          ? { ...n, read: true }
          : n,
      );
    },
    notificationMarkedUnread(state, action: PayloadAction<Notification>) {
      const notif = action.payload;
      state.items = state.items.map((n: Notification) =>
        (notif.id && n.id === notif.id) || (!notif.id && n.timestamp === notif.timestamp)
          ? { ...n, read: false }
          : n,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(markNotificationRead.fulfilled, (state, action) => {
      const notif = action.payload;
      state.items = state.items.map((n: Notification) =>
        (notif.id && n.id === notif.id) || (!notif.id && n.timestamp === notif.timestamp)
          ? { ...n, read: true }
          : n,
      );
    });

    builder.addCase(markNotificationUnread.fulfilled, (state, action) => {
      const notif = action.payload;
      state.items = state.items.map((n: Notification) =>
        (notif.id && n.id === notif.id) || (!notif.id && n.timestamp === notif.timestamp)
          ? { ...n, read: false }
          : n,
      );
    });

    builder.addCase(markAllNotificationsRead.fulfilled, (state) => {
      state.items = state.items.map((n: Notification) =>
        n.read ? n : { ...n, read: true },
      );
    });

    builder.addCase(deleteNotificationThunk.fulfilled, (state, action) => {
      const notif = action.payload;
      state.items = state.items.filter((n: Notification) =>
        (notif.id && n.id !== notif.id) || (!notif.id && n.timestamp !== notif.timestamp),
      );
      state.count = Math.max(0, state.count - 1);
    });
  },
});

export const {
  notificationsRequested,
  notificationsReceived,
  notificationsFailed,
  notificationMarkedRead,
  notificationMarkedUnread,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;

